import { Link } from '@tanstack/react-router'
import { useAuth } from '../contexts/AuthContext'
import { usePageTitle } from '../hooks/usePageTitle'
import { useMemberProfile } from '../hooks/useMemberProfile'
import {
  ClubLinkButton,
  ClubPage,
  Container,
  EmptyPlate,
  Eyebrow,
  MemberAvatar,
  Scorecard,
  SectionHead,
} from '../components/golf'
import type { MemberProfile, MemberProfileRound } from '../hooks/useMemberProfile'

/**
 * A member's profile (design card `member-profile`, route `/members/:userId`).
 *
 * OUTSIDE the clubhouse standing gate, deliberately: a member who sets their
 * profile `public` is asking for a reader with no account and no standing, and
 * a page behind `/clubhouse/*` would bounce exactly that reader. The gate here
 * is the profile owner's own setting, applied by the API per request
 * (docs/fireside/member-profiles.md).
 *
 * The screen renders ONE dead state for every refusal, and the wording is
 * load-bearing: the API answers a private profile and a member who never
 * existed identically, so this page must not say "this member is private" —
 * it does not know that, and saying it would leak precisely what the uniform
 * 404 exists to protect.
 *
 * Nothing here is invented. A member with no posted rounds gets an empty
 * plate, not a placeholder history — the same refusal the rest of the club
 * makes (golf-design-system.md §5).
 */

function formatPlayedOn(iso: string | null): string {
  if (!iso) return 'Date not recorded'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}

function formatIndex(index: number | null): string {
  if (index === null) return 'Unrated'
  return index < 0 ? `+${Math.abs(index).toFixed(1)}` : index.toFixed(1)
}

/**
 * The profile owner's card for one round, hole by hole.
 *
 * `isYou` is about the READER, not the owner: the tinted row and its sr-only
 * "Your standing:" marker say "this is your score", so they belong only to a
 * member reading their own profile. RoundDetailScreen and TheRecordScreen
 * decide it the same way, from the viewer.
 */
function RoundCard({
  round,
  name,
  isYou,
}: {
  round: MemberProfileRound
  name: string
  isYou: boolean
}) {
  const holes = round.scores.map((score) => score.hole_number)
  const columns = [
    { key: 'label', label: 'Hole' },
    ...holes.map((hole) => ({ key: `h${hole}`, label: String(hole), numeric: true })),
    { key: 'total', label: 'Total', numeric: true },
  ]
  const cells = (
    pick: (index: number) => React.ReactNode,
    total: React.ReactNode,
  ): Record<string, React.ReactNode> => ({
    ...Object.fromEntries(holes.map((hole, index) => [`h${hole}`, pick(index)])),
    total,
  })

  return (
    <article className="rounded-club border border-ink/12 bg-parchment p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-x-5 gap-y-1.5 border-b border-brass pb-3">
        <p className="font-display text-[19px] text-pine">{round.course_name}</p>
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-soft">
          {formatPlayedOn(round.played_on)} · {round.holes_count} holes
          {round.tee_name ? ` · ${round.tee_name}` : ''}
        </p>
      </div>

      <dl className="flex flex-wrap gap-x-9 gap-y-3 pt-4">
        {[
          { k: 'Strokes', v: round.total_strokes ?? '—' },
          { k: 'Differential', v: round.score_differential?.toFixed(1) ?? '—' },
          { k: 'Course handicap', v: round.course_handicap ?? '—' },
        ].map((figure) => (
          <div key={figure.k}>
            <dd className="font-display text-[26px] leading-none tabular-nums text-pine">
              {figure.v}
            </dd>
            <dt className="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-brass-deep">
              {figure.k}
            </dt>
          </div>
        ))}
      </dl>

      {round.scores.length > 0 && (
        // Wide content scrolls inside its own container: eighteen holes will
        // not fit a phone, and the page body must never scroll sideways.
        <div className="mt-5 overflow-x-auto">
          <Scorecard
            caption={`${name}'s card`}
            columns={columns}
            rows={[
              {
                id: 'strokes',
                isYou,
                cells: {
                  label: 'Strokes',
                  ...cells(
                    (index) => round.scores[index].strokes ?? '—',
                    round.total_strokes ?? '—',
                  ),
                },
              },
              {
                id: 'putts',
                cells: {
                  label: 'Putts',
                  ...cells(
                    (index) => round.scores[index].putts ?? '—',
                    round.scores.reduce((sum, score) => sum + (score.putts ?? 0), 0) || '—',
                  ),
                },
              },
            ]}
          />
        </div>
      )}

      <div className="mt-6">
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brass-deep">
          Who played
        </p>
        <ul className="mt-2.5 divide-y divide-brass/25">
          {round.players.map((player, index) => (
            <li
              key={player.user_id ?? `unnamed-${index}`}
              className="flex items-baseline justify-between gap-5 py-2"
            >
              {/*
                A withheld name is set in the secondary voice rather than the
                display face — it is not somebody's name, and drawing it like
                one invites a reader to treat "A GTG GC member" as a person.

                Which of the two it is, is the SERVER's answer: `named`. It
                cannot be derived here. `user_id` is withheld along with the
                name, so keying on it paints a guest's real, typed name
                ("Dell") as a redaction; and `guest` is true both for that
                guest and for one anonymised to the open web, so keying on it
                does the opposite and dresses a placeholder as a person.
              */}
              <span
                className={
                  player.named
                    ? 'font-display text-[15px] text-pine'
                    : 'text-[14px] italic text-ink-soft'
                }
              >
                {player.display_name}
              </span>
              <span className="tabular-nums text-[14px] text-ink">
                {player.total_strokes ?? '—'}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </article>
  )
}

function ProfileBody({ profile, isYou }: { profile: MemberProfile; isYou: boolean }) {
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-x-7 gap-y-5 rounded-club border border-ink/12 bg-parchment p-6.5">
        <div className="flex items-center gap-5">
          <MemberAvatar name={profile.display_name} mark={profile.club_mark} size="lg" />
          <div>
            {/*
              The page's h1, which the success state did without: the member's
              name was a `<p>`, so heading navigation skipped the member and the
              outline started below them. The refusal state always had one.

              usePageTitle reads `main h1` for its focus move as well, but that
              effect runs once at mount, while this screen is still loading and
              neither h1 exists — so it is the outline this fixes, not the focus
              target, which keeps falling back to `main`.
            */}
            <h1 className="font-display text-[27px] text-pine">{profile.display_name}</h1>
            <p className="mt-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-soft">
              {[
                profile.member_number ? `No. ${profile.member_number}` : null,
                `${profile.rounds_count} ${profile.rounds_count === 1 ? 'round' : 'rounds'} in the book`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
            {isYou && profile.visibility && (
              <p className="mt-2.5">
                <span className="inline-block whitespace-nowrap rounded-club-sm border border-brass-deep px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-brass-deep">
                  {profile.visibility === 'public'
                    ? 'Anyone may read this'
                    : profile.visibility === 'members'
                      ? 'Members may read this'
                      : 'Only you may read this'}
                </span>
              </p>
            )}
          </div>
        </div>

        <div className="text-right">
          <p className="font-display text-[40px] leading-none tabular-nums text-pine">
            {formatIndex(profile.handicap_index)}
          </p>
          <p className="mt-1.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brass-deep">
            Handicap Index
          </p>
        </div>
      </div>

      {isYou && (
        <p className="mt-4 text-[13px] text-ink-soft">
          This is your own book.{' '}
          <Link className="text-fairway underline" to="/clubhouse/account">
            Change who may read it
          </Link>
          .
        </p>
      )}

      <section className="mt-13">
        <SectionHead eyebrow="The book" title="Rounds entered" />
        {profile.rounds.length === 0 ? (
          <EmptyPlate
            title="No rounds in the book yet."
            description="A round appears here once it has been played, completed and posted to the clubhouse. Nothing else is shown."
          />
        ) : (
          <div className="grid gap-4.5">
            {profile.rounds.map((round) => (
              <RoundCard key={round.id} round={round} name={profile.display_name} isYou={isYou} />
            ))}
          </div>
        )}
      </section>
    </>
  )
}

export function MemberProfileScreen({ userId }: { userId: string }) {
  const { user } = useAuth()
  const { profile, absent, isLoading, error } = useMemberProfile(userId)
  usePageTitle(profile ? profile.display_name : 'A member')

  return (
    <ClubPage chrome="clubhouse">
      <Container className="py-16">
        <Eyebrow>The Register</Eyebrow>

        {isLoading && <p className="mt-8 text-[14.5px] text-ink-soft">Reading the book…</p>}

        {/*
          One dead state for every refusal. The server answers a private
          profile exactly as it answers a member who does not exist, and this
          copy must keep that promise — naming privacy here would confirm the
          account the 404 refuses to confirm.
        */}
        {!isLoading && absent && (
          <div className="mt-8 max-w-[52ch]">
            <h1 className="mb-3.5 font-display text-[clamp(2.25rem,4.4vw,3.25rem)] font-medium leading-[1.04] text-pine">
              No such page in the book.
            </h1>
            <p className="mb-7 text-[15.5px] text-ink-soft">
              There is nothing here to read. If you were sent this link by a member, ask them
              for it again — and if you are a member yourself, signing in may be all that is
              missing.
            </p>
            <ClubLinkButton to="/clubhouse" variant="ghost">
              Back to the clubhouse
            </ClubLinkButton>
          </div>
        )}

        {!isLoading && error && !absent && (
          <p role="alert" className="mt-8 text-[15px] text-flag">
            {error}
          </p>
        )}

        {!isLoading && profile && (
          <div className="mt-8">
            <ProfileBody profile={profile} isYou={user?.id === profile.user_id} />
          </div>
        )}
      </Container>
    </ClubPage>
  )
}
